import { LandingBlockType } from '../types'
import { AVA_GUARANTEES_CONFIG } from './landingTemplates'

export interface BlockDefinition {
  type: LandingBlockType
  label: string
  description: string
  icon: string
  removable: boolean
  defaultConfig: Record<string, unknown>
  /** Agrupación mostrada en la biblioteca del constructor. */
  category?: string
}

export const BLOCK_LIBRARY: BlockDefinition[] = [
  {
    type: 'topbar',
    label: 'Barra superior',
    description: 'Logo fijo y botón de acción.',
    icon: 'PanelTop',
    removable: true,
    defaultConfig: {
      ctaLabel: 'Quiero información',
      sticky: true,
      showLogo: true,
      // Visibilidad del acceso «Iniciar sesión» de la barra superior.
      showAuth: true,
      logoHeight: 34,
      logoAlign: 'left',
    },
  },
  {
    type: 'logo',
    label: 'Logo',
    description: 'Logotipo centrado.',
    icon: 'Sparkles',
    removable: true,
    defaultConfig: { align: 'center', logoHeight: 34 },
  },
  {
    type: 'hero',
    label: 'Hero',
    description: 'Gancho principal con subtítulo.',
    icon: 'Rocket',
    removable: true,
    defaultConfig: {
      eyebrow: '',
      brandLine: '',
      title: 'Un titular que capte la atención de tu cliente ideal',
      highlight: '',
      subtitle: 'Explica en una frase qué consigue tu cliente contigo.',
      subtitleHighlight: '',
      extraLine: '',
      showLogo: true,
      logoHeight: 34,
    },
  },
  {
    type: 'titulo',
    label: 'Título',
    description: 'Bloque de texto destacado.',
    icon: 'Heading1',
    removable: true,
    defaultConfig: { text: 'Título de sección', align: 'center' },
  },
  {
    type: 'subtitulo',
    label: 'Subtítulo',
    description: 'Texto complementario.',
    icon: 'Text',
    removable: true,
    defaultConfig: { text: 'Texto de apoyo para tu sección.', align: 'center' },
  },
  {
    type: 'imagen',
    label: 'Imagen',
    description: 'Imagen destacada.',
    icon: 'Image',
    removable: true,
    defaultConfig: { url: '', alt: 'Imagen de la landing', rounded: true },
  },
  {
    type: 'video',
    label: 'Vídeo',
    description: 'Vídeo replay 16:9 con miniatura.',
    icon: 'PlayCircle',
    removable: true,
    defaultConfig: {
      url: '',
      thumbnailUrl: '',
      overlayTitle: '',
      overlayHighlight: '',
      note: '',
      caption: '',
      ctaLabel: '',
    },
  },
  {
    type: 'beneficios',
    label: 'Beneficios',
    description: 'Tarjetas con iconos.',
    icon: 'LayoutGrid',
    removable: true,
    defaultConfig: {
      title: 'Qué consigues',
      items: [
        { icon: 'Target', title: 'Beneficio 1', text: 'Describe el beneficio.' },
        { icon: 'Video', title: 'Beneficio 2', text: 'Describe el beneficio.' },
      ],
    },
  },
  {
    type: 'estadisticas',
    label: 'Estadísticas',
    description: 'Cifras destacadas.',
    icon: 'BarChart3',
    removable: true,
    defaultConfig: {
      items: [
        { value: '+120', label: 'Clientes' },
        { value: '3,4x', label: 'Retorno medio' },
      ],
    },
  },
  {
    type: 'testimonios',
    label: 'Testimonios',
    description: 'Prueba social de clientes.',
    icon: 'Quote',
    removable: true,
    defaultConfig: { title: 'Lo que dicen nuestros clientes', items: [] },
  },
  {
    type: 'logos_clientes',
    label: 'Logotipos de clientes',
    description: 'Marcas que confían en ti.',
    icon: 'Building2',
    removable: true,
    defaultConfig: { title: 'Empresas que ya confían en nosotros', items: [] },
  },
  {
    type: 'formulario',
    label: 'Formulario',
    description: 'Formulario asociado a la landing.',
    icon: 'ClipboardList',
    removable: true,
    defaultConfig: {
      title: 'Solicita tu estrategia',
      subtitle: 'Responde unas preguntas rápidas y te contactamos.',
      /** «si» o «no»: barra de progreso y aviso de privacidad. */
      showProgress: 'si',
      showPrivacy: 'si',
    },
  },
  {
    type: 'faq',
    label: 'Preguntas frecuentes',
    description: 'Resuelve objeciones habituales.',
    icon: 'HelpCircle',
    removable: true,
    defaultConfig: { title: 'Preguntas frecuentes', items: [] },
  },
  {
    type: 'calendario',
    label: 'Calendario',
    description: 'Invitación a agendar una llamada.',
    icon: 'CalendarDays',
    removable: true,
    defaultConfig: {
      title: 'Agenda una llamada',
      text: 'Reserva 20 minutos con nuestro equipo.',
      url: '',
      ctaLabel: 'Reservar hora',
    },
  },
  {
    type: 'whatsapp',
    label: 'WhatsApp',
    description: 'Botón flotante de contacto.',
    icon: 'MessageCircle',
    removable: true,
    defaultConfig: { number: '', message: 'Hola, quiero más información' },
  },
  {
    type: 'cta_final',
    label: 'CTA final',
    description: 'Llamada a la acción de cierre.',
    icon: 'MousePointerClick',
    removable: true,
    defaultConfig: {
      title: '¿Empezamos?',
      text: 'Da el primer paso hoy mismo.',
      ctaLabel: 'Quiero empezar',
    },
  },
  {
    type: 'garantias',
    label: 'Por qué implementar AVA',
    category: 'Conversión',
    description:
      'Acordeones de garantías: implementación en 10 días, Garantía AVA 365 y exclusividad por zona.',
    icon: 'ShieldCheck',
    removable: true,
    // Misma definición maestra que usan las plantillas y la migración.
    defaultConfig: AVA_GUARANTEES_CONFIG,
  },
  {
    type: 'pilares',
    label: 'Tres pilares',
    description:
      'Tres columnas con icono sobre panel suave. En móvil caben en una única fila.',
    icon: 'Columns3',
    removable: true,
    defaultConfig: {
      items: [
        {
          icon: 'Magnet',
          title: 'Atraemos',
          text: 'Describe cómo atraes clientes.',
          mobileText: 'Más oportunidades.',
        },
        {
          icon: 'Filter',
          title: 'Viralizamos',
          text: 'Describe tu estrategia de contenido.',
          mobileText: 'Contenido que posiciona.',
        },
        {
          icon: 'TrendingUp',
          title: 'Automatizamos',
          text: 'Describe tu automatización.',
          mobileText: 'IA que responde 24/7.',
        },
      ],
      /** «fila» (una única fila de tres columnas) o «apilado». */
      mobileLayout: 'fila',
      showMobileText: 'si',
      mobileIconSize: 24,
      mobileCircleSize: 46,
      mobileTitleSize: 12,
      mobileTextSize: 10,
      mobileGap: 6,
      mobilePadding: 2,
    },
  },
  {
    type: 'confianza',
    label: 'Franja de confianza',
    description: 'Cuatro puntos breves de confianza con icono.',
    icon: 'ShieldCheck',
    removable: true,
    defaultConfig: {
      items: [
        { icon: 'ShieldCheck', text: 'Datos 100 % seguros' },
        { icon: 'Zap', text: 'Implementación rápida' },
        { icon: 'BadgeCheck', text: 'Sistema probado' },
        { icon: 'Headphones', text: 'Soporte experto' },
      ],
    },
  },
  {
    type: 'frase',
    label: 'Frase destacada',
    description: 'Frase de posicionamiento con líneas laterales.',
    icon: 'Minus',
    removable: true,
    defaultConfig: {
      text: 'Diseñado para empresas que quieren crecer sin depender del boca a boca',
    },
  },
  {
    type: 'servicios',
    label: 'Servicios',
    description: 'Lista de servicios con descripción.',
    icon: 'Briefcase',
    removable: true,
    defaultConfig: {
      title: 'Nuestros servicios',
      subtitle: '',
      items: [
        { icon: 'Target', title: 'Servicio 1', text: 'Describe el servicio.' },
        { icon: 'Sparkles', title: 'Servicio 2', text: 'Describe el servicio.' },
        { icon: 'Megaphone', title: 'Servicio 3', text: 'Describe el servicio.' },
      ],
    },
  },
  {
    type: 'caracteristicas',
    label: 'Características',
    description: 'Listado en dos columnas con checks.',
    icon: 'ListChecks',
    removable: true,
    defaultConfig: {
      title: 'Todo lo que incluye',
      items: [
        { title: 'Característica 1', text: 'Detalle de la característica.' },
        { title: 'Característica 2', text: 'Detalle de la característica.' },
      ],
    },
  },
  {
    type: 'galeria',
    label: 'Galería',
    description: 'Rejilla de imágenes.',
    icon: 'Images',
    removable: true,
    defaultConfig: { title: 'Galería', items: [] },
  },
  {
    type: 'precios',
    label: 'Precios',
    description: 'Planes con precio y ventajas.',
    icon: 'CreditCard',
    removable: true,
    defaultConfig: {
      title: 'Planes',
      subtitle: '',
      items: [
        {
          name: 'Starter',
          price: '490 €',
          period: 'al mes',
          features: 'Landing y formulario\nCRM básico\nSoporte por email',
          featured: '',
          ctaLabel: 'Empezar',
        },
        {
          name: 'Growth',
          price: '990 €',
          period: 'al mes',
          features: 'Todo lo anterior\nCampañas gestionadas\nAutomatización con IA',
          featured: 'sí',
          ctaLabel: 'Quiero este plan',
        },
      ],
    },
  },
  {
    type: 'contacto',
    label: 'Contacto',
    description: 'Datos de contacto de la empresa.',
    icon: 'AtSign',
    removable: true,
    defaultConfig: {
      title: 'Hablemos',
      text: 'Estamos disponibles de lunes a viernes.',
      email: '',
      phone: '',
      address: '',
    },
  },
  {
    /*
     * Comparativa de cualificación. NO forma parte de ninguna plantilla: solo
     * está disponible en «Añadir sección» para que el usuario decida.
     */
    type: 'filtro',
    label: 'Es para ti / No es para ti',
    category: 'Conversión',
    description: 'Dos columnas que filtran al cliente ideal antes de agendar.',
    icon: 'ListChecks',
    removable: true,
    defaultConfig: {
      eyebrow: 'ANTES DE AGENDAR',
      title: 'Esto no es para todo el mundo',
      subtitle: 'Prefiero decírtelo claro para no hacerte perder el tiempo.',
      yesTitle: 'ES PARA TI SI...',
      noTitle: 'NO ES PARA TI SI...',
      items: [
        {
          lado: 'si',
          text: 'Quieres generar más oportunidades para tu negocio de forma constante.',
        },
        {
          lado: 'si',
          text: 'Estás cansado de depender únicamente del boca a boca.',
        },
        {
          lado: 'si',
          text: 'Quieres utilizar contenido, publicidad e inteligencia artificial para crecer.',
        },
        {
          lado: 'si',
          text: 'Buscas un sistema completo de captación y seguimiento, no acciones aisladas.',
        },
        {
          lado: 'no',
          text: 'Buscas resultados sin implementar ningún cambio en tu negocio.',
        },
        {
          lado: 'no',
          text: 'No estás dispuesto a invertir en captación y crecimiento.',
        },
        {
          lado: 'no',
          text: 'Solo buscas seguidores y no oportunidades comerciales.',
        },
        { lado: 'no', text: 'No quieres medir, mejorar ni seguir un sistema.' },
      ],
    },
  },
  {
    /* Historia / Sobre mí. Tampoco se añade a ninguna plantilla. */
    type: 'historia',
    label: 'Historia / Sobre mí',
    category: 'Contenido',
    description: 'Foto, historia y CTA opcional. Dos variantes de diseño.',
    icon: 'UserRound',
    removable: true,
    defaultConfig: {
      variant: 'editorial',
      eyebrow: 'CONOCE AL EQUIPO',
      title: 'Quién está detrás de Sistema AVA',
      photo: '',
      photoSize: 320,
      photoShape: 'redondeada',
      name: '',
      role: '',
      text: 'Llevo años trabajando con negocios y construyendo sistemas de captación digital.\n\nSistema AVA nace de unir tres piezas que normalmente las empresas trabajan por separado: contenido, publicidad y automatización.\n\nEl objetivo no es conseguir simplemente más visitas o seguidores, sino crear un sistema que genere oportunidades comerciales de forma constante y permita al equipo centrarse en vender y atender mejor a sus clientes.',
      textWidth: 680,
      ctaLabel: '',
      ctaUrl: '',
    },
  },
  {
    type: 'footer',
    label: 'Footer',
    description: 'Pie de página y legales.',
    icon: 'PanelBottom',
    removable: true,
    defaultConfig: {
      text: '© 2026 Sistema AVA. Todos los derechos reservados.',
      links: ['Política de privacidad', 'Aviso legal', 'Cookies'],
      socials: [],
    },
  },
]

export function blockDefinition(type: LandingBlockType): BlockDefinition {
  return (
    BLOCK_LIBRARY.find((b) => b.type === type) ?? {
      type,
      label: type,
      description: '',
      icon: 'Square',
      removable: true,
      defaultConfig: {},
    }
  )
}
